/**
 * VehicleScanLabelCard — l'étiquette « QR pare-brise » d'un véhicule, côté gestionnaire.
 *
 * Le serveur produit tout (`poolScanAPI.getLabel` → `GET /pool/vehicles/:id/scan-label`) :
 * le jeton signé, l'URL qu'il ouvre, et le QR en SVG. Ici on l'affiche et on l'imprime.
 * Le QR est STABLE : l'imprimer deux fois donne la même étiquette.
 *
 * Réservé aux rôles gestionnaires — le serveur refuse les autres ; la carte ne s'affiche
 * même pas pour eux, par politesse, pas par sécurité.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QrCode, Printer, Loader2 } from 'lucide-react';
import { poolScanAPI } from '../../../api/pool.js';
import { useAuth } from '../../../v3/context/AuthContext.jsx';

export const MANAGER_ROLES = ['manager', 'admin', 'org_admin', 'fleet_manager'];

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function VehicleScanLabelCard({ vehicle }) {
  const { t } = useTranslation();
  const { user, isSuperuser } = useAuth();
  const [label, setLabel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!vehicle?.id || !(isSuperuser || MANAGER_ROLES.includes(user?.role))) return null;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await poolScanAPI.getLabel(vehicle.id);
      setLabel(res?.data || res);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const svgSrc = label?.svg ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(label.svg)}` : null;

  const print = () => {
    const w = window.open('', '_blank', 'width=420,height=560');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(label.license_plate)}</title>
<style>body{font-family:system-ui,sans-serif;text-align:center;margin:24px}img{width:260px;height:260px}
h1{font-size:28px;margin:12px 0 4px}p{font-size:14px;margin:4px 0;color:#333}</style></head><body>
<img src="${svgSrc}" alt="" /><h1>${escapeHtml(label.license_plate)}</h1>
<p>${escapeHtml(t('poolScan.label.printHint'))}</p><p style="font-size:11px;color:#666">${escapeHtml(t('poolScan.label.noUnlock'))}</p>
<script>window.onload=function(){window.print();}</script></body></html>`);
    w.document.close();
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="flex items-center gap-2 font-medium text-gray-900">
        <QrCode className="h-4 w-4 text-gray-500" /> {t('poolScan.label.title')}
      </p>
      <p className="mt-1 text-sm text-gray-500">{t('poolScan.label.description')}</p>

      {!label && (
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
          {t('poolScan.label.show')}
        </button>
      )}

      {error && <p className="mt-2 text-sm text-red-700">{t('poolScan.label.loadFailed', { message: error })}</p>}

      {label && (
        <div className="mt-3 flex flex-col items-center gap-2 sm:flex-row sm:items-start">
          <img src={svgSrc} alt={t('poolScan.label.alt', { plate: label.license_plate })} className="h-40 w-40 rounded border border-gray-200" />
          <div className="min-w-0 space-y-2 text-sm">
            <p className="text-lg font-bold text-gray-900">{label.license_plate}</p>
            <p className="break-all text-xs text-gray-500">{label.url}</p>
            <p className="text-xs text-gray-500">{t('poolScan.label.noUnlock')}</p>
            <button
              type="button"
              onClick={print}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Printer className="h-4 w-4" /> {t('poolScan.label.print')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default VehicleScanLabelCard;
